import React from 'react'

import { GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import styled, { css } from 'styled-components/macro'
import { connect } from 'react-redux'

import { PageLayout } from '@/components/Layout/PageLayout'
import { BrandDescription } from '@/components/BrandDescription/BrandDescription'
import { Text } from '@/components/Text'
import BrandReview from '@/components/Pages/Brand/parts/BrandReview'
import WITB from '@/components/Pages/Brand/parts/WITB'
import { ContactForm } from '@/components/ContactForm'

import { BrandItemCards } from '@/components/BrandItemCards'
import { getSettings } from '@/redux/actions'

import { initializeStore } from '@/redux/store'

import { banners } from '@/static/Brands/Banners/banners'
import { BRAND_DATA } from '@/static/Brands/data'
import { BRAND_CONTACTS } from '@/static/General'

import { retrieve } from '@/utils/Helpers'
import { ColorsMap, device } from '@/utils/Theme'

import { StateInterface } from '@/types/State'

const filterMediaByBrand = (slug, items) => {
  return items.filter((item) => item.brand === slug)
}

const ProductPage = ({ state, brand, catalogues: _catalogues, galleries: _galleries }) => {
  const router = useRouter()
  const { t, lang } = useTranslation('common')

  const { brand: brandQuery } = router.query

  const handleSubmit = async (e: React.FormEvent, payload, actions): Promise<boolean> => {
    e.preventDefault()

    actions.setLoading(true)
    if (actions.validate()) {
      const data = {
        name: payload.name,
        club: payload.club,
        email: payload.email,
        phone: payload.phone,
        message: payload.content,
        send_to: payload.sendTo,
      }

      await fetch('/api/email', {
        method: 'post',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            alert(t('message__form_success'))
          }
        })

      actions.setLoading(false)

      return true
    }
    actions.setLoading(false)

    return false
  }

  const brandData = BRAND_DATA[brand.slug] || {}

  const catalogues = filterMediaByBrand(brand.slug, _catalogues)
  const galleries = filterMediaByBrand(brand.slug, _galleries)

  return (
    <PageLayout title={`${brand.name} | ${state.settings.name}`} secondary HeaderbackgroundGrey>
      {brand && (
        <BrandDescription
          banner={banners[brand.slug] ? banners[brand.slug] : [`/images/banners/${brand.slug}.jpg`]}
          content={brand.translations[lang].description || brand.description}
        />
      )}

      {brandData.videoId && (
        <SectionWrapper $pb="4rem">
          <VideoIFrame src={`https://www.youtube.com/embed/${brandData.videoId}?autoplay=1&mute=1`} />
        </SectionWrapper>
      )}

      {brandQuery === 'dachstein' && (
        <SectionWrapper $pb="3.5rem">
          <ImageWrapper>
            <BrandImage src="/images/dachstein-workbook-100-years.jpg" />
            <BrandImage src="/images/dachstein-workbook-history.jpg" />
          </ImageWrapper>
        </SectionWrapper>
      )}

      {brandQuery === 'cobra-puma-golf' && (
        <>
          <SectionWrapper $pt="4rem" $bg={ColorsMap.primary}>
            <Text as="h2" fontSize="xxxl" fontWeight="bold" textColor="white" textAlign="center">
              {t('fowler-witb')}
            </Text>
            <WITB player="fowler" />
          </SectionWrapper>

          <SectionWrapper $pt="4rem" $bg={ColorsMap.primary}>
            <Text as="h2" fontSize="xxxl" fontWeight="bold" textColor="white" textAlign="center">
              {t('beck-witb')}
            </Text>
            <Text as="h3" fontSize="xxl" fontWeight="bold" textColor="white" textAlign="center">
              {t('beck-desc')}
            </Text>
            <WITB player="beck" />
          </SectionWrapper>

          <SectionWrapper $py="4rem" $bg={ColorsMap.primary}>
            <Text as="h2" fontSize="xxxl" fontWeight="bold" textColor="white" textAlign="center">
              {t('homa-witb')}
            </Text>
            <WITB player="homa" />
          </SectionWrapper>
        </>
      )}

      {brandQuery === 'lignum-tee' && (
        <>
          <SectionWrapper $pt="2rem" $py="4rem">
            <BannerWrapper>
              <Banner src="/images/banners/lignum_1.png" alt="" />
              <Banner src="/images/banners/lignum_2.png" alt="Lignum Tee ecoline" />
            </BannerWrapper>
          </SectionWrapper>
        </>
      )}

      {brandData.reviews && (
        <SectionWrapper $py="4rem" $bg={brandQuery === 'cobra-puma-golf' ? ColorsMap.shadowGrey : null}>
          <Text as="h2" fontSize="xxl" fontWeight="bold" textColor="primary" textAlign="center">
            {t('reviews')}
          </Text>
          <Reviews>
            {brandData.reviews?.map((review) => (
              <BrandReview review={review} />
            ))}
          </Reviews>
        </SectionWrapper>
      )}

      {catalogues.length || galleries.length ? (
        <SectionWrapper $bg={ColorsMap.primary} $py="2rem">
          {!!catalogues.length && (
            <div style={{ padding: '2rem 0' }}>
              <Text
                as="h2"
                fontSize="xxl"
                fontWeight="bold"
                textColor="white"
                textAlign="center"
                style={{ textShadow: '#509050 1px 0 20px' }}
              >
                {t('catalogues')}
              </Text>
              <BrandItemCards brand={brand.slug} items={catalogues} />
              {brandQuery === 'cobra-puma-golf' && (
                <CobraPumaBannersWrapper>
                  <img src="/images/banners/cobra-puma-golf_1.jpg" alt="" />
                  <img src="/images/banners/cobra-puma-golf_2.jpg" alt="" />
                  <img src="/images/banners/cobra-puma-golf_3.jpg" alt="" />
                </CobraPumaBannersWrapper>
              )}
            </div>
          )}
          {!!galleries.length && (
            <div style={{ padding: '2rem 0' }}>
              <Text
                as="h2"
                fontSize="xxl"
                fontWeight="bold"
                textColor="white"
                textAlign="center"
                style={{ textShadow: '#509050 1px 0 20px' }}
              >
                {t('gallery')}
              </Text>
              <BrandItemCards brand={brand.slug} items={galleries} />
            </div>
          )}
        </SectionWrapper>
      ) : null}

      {BRAND_CONTACTS[brand.slug] && (
        <SectionWrapper $pt="2rem" $pb="0">
          <Text as="h2" fontSize="xxl" fontWeight="bold" textColor="primary" textAlign="center">
            {t('contact')}
          </Text>
          <ContactForm contact={BRAND_CONTACTS[brand.slug]} handleSubmit={handleSubmit} />
        </SectionWrapper>
      )}
    </PageLayout>
  )
}

const mapStateToProps = (state: StateInterface) => ({
  state,
})

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const reduxStore = initializeStore({})
  const { dispatch } = reduxStore
  const { brand } = ctx.query

  await dispatch(getSettings({}))

  let error = false
  const fetchedBrand = await retrieve(`api/brand?slug=${brand}`, 'GET', null)
    .then((res) => {
      if (res.status === 404) {
        throw new Error('')
      }
      return res.json()
    })
    .then((res) => {
      if (res.data == undefined) {
        throw Error('')
      }
      return res.data
    })
    .catch(() => {
      error = true
    })

  if (error) {
    return { notFound: true }
  }

  const catalogues = await retrieve('resource/catalogues', 'GET').then((res) => {
    if (res.status >= 400) return []
    return res.json()
  })

  const galleries = await retrieve('resource/galleries', 'GET').then((res) => {
    if (res.status >= 400) return []
    return res.json()
  })

  return {
    props: {
      initialReduxState: reduxStore.getState(),
      brand: fetchedBrand,
      catalogues,
      galleries,
    },
  }
}

export default connect(mapStateToProps)(ProductPage)

const VideoIFrame = styled.iframe`
  width: 100%;
  aspect-ratio: 16/9;
  border: none;
`

type SectionProps = {
  $py?: string
  $pt?: string
  $pb?: string
  $bg?: string
}
const SectionWrapper = styled.div<SectionProps>`
  padding: 0 13%;
  ${({ $py }) =>
    $py &&
    css`
      padding-top: ${$py} !important;
      padding-bottom: ${$py} !important;
    `}
  ${({ $pt }) =>
    $pt &&
    css`
      padding-top: ${$pt} !important;
    `} ${({ $pb }) =>
    $pb &&
    css`
      padding-bottom: ${$pb} !important;
    `} ${({ $bg }) =>
    $bg &&
    css`
      background-color: ${$bg};
      background-image: url(/images/dots_white.png);
    `} @media (
    max-width: ${device.mobileS}) {
    padding: 0 1rem;
  }
`

const CobraPumaBannersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`

const Reviews = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 2rem;

  @media (max-width: ${device.midleDesktop}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${device.tablet}) {
    grid-template-columns: 1fr;
  }
`

const ImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3.5rem;
`

const BrandImage = styled.img`
  width: 100%;
`

const BannerWrapper = styled.div`
  display: grid;
  width: 100%;
  gap: 2rem;
  grid-template-columns: repeat(2, 1fr);

  @media (max-width: ${device.tablet}) {
    grid-template-columns: 1fr;
  }
`

const Banner = styled.img`
  width: 100%;
  aspect-ratio: 2 / 1;
`
